import { createPortal } from "react-dom";
import { scoreTown } from "../lib/scoring.js";
import { hardCriteriaCheck, STATUS_META } from "../lib/hardCriteria.js";
import { prettyConcelho } from "../lib/localization.js";

// Schone labels per harde-eis (zonder de waarde erin, zodat de rij
// voor alle plekken hetzelfde heet).
const CRIT_LABELS = {
  sea: "Zee ≤ 30 min", montessori: "Montessori < 30 km", heat: "Zomerhitte",
  dryness: "Droog genoeg", nature: "Groen / bergachtig", fire: "Brandrisico",
};

export function CompareDashboard({ towns, concelhoBySlug, weights, onOpenTown, onClose }) {
  if (!towns.length) return null;

  const cols = towns.map(t => {
    const concelho = concelhoBySlug[t.concelho];
    return {
      t, concelho,
      pct: Math.round(scoreTown(t, weights, concelho).pct),
      crit: hardCriteriaCheck(t, concelho),
      cl: concelho?.climate || {},
    };
  }).sort((a, b) => b.pct - a.pct);

  // Volgorde van harde eisen (op basis van eerste kolom).
  const critIds = [];
  for (const c of cols) for (const it of c.crit) if (!critIds.includes(it.id)) critIds.push(it.id);

  const n = cols.length;
  const gridCols = `minmax(96px,1.1fr) repeat(${n}, minmax(0,1fr))`;
  const best = Math.max(...cols.map(c => c.pct));

  const Cell = ({ children, head, last }) => (
    <div style={{ padding:"11px 8px", textAlign:"center", fontSize:13,
      borderBottom: last ? "none" : "1px solid #f1efe9",
      display:"flex", alignItems:"center", justifyContent:"center" }}>{children}</div>
  );
  const Label = ({ children, last }) => (
    <div style={{ padding:"11px 12px", fontSize:13, color:"#6a6a66", fontWeight:500,
      borderBottom: last ? "none" : "1px solid #f1efe9", display:"flex", alignItems:"center" }}>{children}</div>
  );
  const critRow = (id, i, total) => {
    const label = CRIT_LABELS[id] || id;
    return (
      <div key={id} style={{ display:"contents" }}>
        <Label>{label}</Label>
        {cols.map(c => {
          const it = c.crit.find(x => x.id === id);
          const m = it ? (STATUS_META[it.status] || STATUS_META.info) : null;
          return <Cell key={c.t.name}>{m ? <span style={{ color:m.color, fontSize:17, fontWeight:700 }}>{m.symbol}</span> : "–"}</Cell>;
        })}
      </div>
    );
  };
  const factRow = (label, fn, last) => (
    <div style={{ display:"contents" }}>
      <Label last={last}>{label}</Label>
      {cols.map(c => <Cell key={c.t.name} last={last}>{fn(c)}</Cell>)}
    </div>
  );

  return createPortal(
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position:"fixed", inset:0, zIndex:720, background:"rgba(0,0,0,.5)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div style={{ background:"#fff", width:"100%", maxWidth:760, height:"100dvh",
                    display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                      padding:"calc(14px + env(safe-area-inset-top,0px)) 16px 12px",
                      borderBottom:"1px solid #eee", flexShrink:0, background:"#fff" }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700 }}>Vergelijken</h2>
          <button onClick={onClose} aria-label="Sluiten"
            style={{ width:34, height:34, borderRadius:"50%", border:"none", background:"#f1efe9",
                     fontSize:20, color:"#333", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
        </div>

        <div style={{ flex:1, overflowY:"auto", padding:"4px 12px 24px" }}>
        {cols.length >= 2 && (() => {
          const lead = [];
          const by = (label, fn, dir) => {
            const v = cols.filter(c => fn(c) != null);
            if (v.length < 2) return;
            const best = v.reduce((a, b) => (dir === "max" ? (fn(b) > fn(a) ? b : a) : (fn(b) < fn(a) ? b : a)));
            lead.push({ label, name: best.t.name });
          };
          by("🏅 hoogste score", c => c.pct, "max");
          by("☀ meeste zon", c => c.cl.annual_sunshine_h, "max");
          by("🌧 droogst", c => c.cl.annual_rainfall_mm, "min");
          by("🌊 dichtst bij zee", c => c.t.hardRequirements?.seaDistanceMin, "min");
          by("🔥 laagst brandrisico", c => c.concelho?.fire_risk_class, "min");
          if (!lead.length) return null;
          return (
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, padding:"10px 4px 12px" }}>
              {lead.map((l, i) => (
                <span key={i} style={{ fontSize:12, background:"#f4f2ec", borderRadius:14, padding:"5px 11px", color:"#444" }}>
                  {l.label}: <b>{l.name}</b>
                </span>
              ))}
            </div>
          );
        })()}

          <div style={{ display:"grid", gridTemplateColumns: gridCols, alignItems:"stretch" }}>
            {/* Kop met plaatsnamen + score (blijft staan bij scrollen) */}
            <div style={{ borderBottom:"1px solid #eee", position:"sticky", top:0, background:"#fff", zIndex:2 }} />
            {cols.map(c => (
              <button key={c.t.name} onClick={() => onOpenTown(c.t)}
                style={{ border:"none", cursor:"pointer", padding:"10px 6px 12px", textAlign:"center",
                         borderBottom:"1px solid #eee", borderLeft:"1px solid #f4f2ec",
                         position:"sticky", top:0, background:"#fff", zIndex:2 }}>
                <div style={{ fontSize:13, fontWeight:700, lineHeight:1.2 }}>{c.t.name}</div>
                <div style={{ fontSize:10, color:"#999", marginBottom:6 }}>{c.t.region}</div>
                <div style={{ display:"inline-flex", flexDirection:"column", alignItems:"center" }}>
                  <span style={{ fontSize:18, fontWeight:700,
                    color: c.pct>=70?"#3b7a35":c.pct>=50?"#c17f2a":"#c94040" }}>{c.pct}</span>
                  {c.pct === best && n > 1 && <span style={{ fontSize:9, color:"#3b7a35", fontWeight:600 }}>beste</span>}
                </div>
              </button>
            ))}

            {/* Harde eisen */}
            {critIds.map(id => critRow(id))}

            {/* Feiten */}
            {factRow("Gemeente", c => prettyConcelho(c.t.concelho))}
            {factRow("🌧 Neerslag", c => c.cl.annual_rainfall_mm != null ? `${c.cl.annual_rainfall_mm} mm` : "–")}
            {factRow("☀ Zon", c => c.cl.annual_sunshine_h != null ? `${c.cl.annual_sunshine_h} h` : "–")}
            {factRow("🌊 Zee", c => c.t.hardRequirements?.seaDistanceMin != null ? `${c.t.hardRequirements.seaDistanceMin} min` : "–", true)}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
